import { z } from 'zod/v3';
import {
  getFromMapSafely,
  getMapBy,
  type UUIDType,
} from '../../../../common/utils';
import type { AlgorithmJoinsMutationDtoType } from '../../joins/schema/types';
import { AlgorithmMappingMutationDtoSchema } from '../../mappings/schema/AlgorithmMappingMutationDto';
import type { AlgorithmMappingMutationDtoType } from '../../mappings/schema/types';
import { decodeDimensionCodeValue, getHandleId, parseHandleId } from '../utils';
import {
  AlgorithmJoinsAndMappingsGraphEdgeSchema,
  AlgorithmJoinsAndMappingsGraphNodeSchema,
} from './AlgorithmJoinsAndMappingsGraphValidFormDataSchema';
import type {
  AlgorithmJoinsAndMappingsGraphEdgeType,
  AlgorithmJoinsAndMappingsGraphNodeOfType,
} from './types';
import { resolveOwnerNode } from './utils';

export const AlgorithmJoinsAndMappingsGraphMutationDtoSchema = z
  .object({
    nodes: z.array(AlgorithmJoinsAndMappingsGraphNodeSchema),
    edges: z.array(AlgorithmJoinsAndMappingsGraphEdgeSchema),
  })
  .transform(({ nodes, edges }) => {
    const nodesById = getMapBy(nodes, 'id');
    const edgesByTargetId = edges.reduce(
      (map, edge) =>
        map.set(edge.target, (map.get(edge.target) || []).concat(edge)),
      new Map<UUIDType, AlgorithmJoinsAndMappingsGraphEdgeType[]>(),
    );

    const algorithmSourceNodes: AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>[] =
      nodes.filter((n) => n.type === 'algorithmSource');

    const joinsByNodeId = new Map<
      UUIDType,
      AlgorithmJoinsMutationDtoType['joins'][number]
    >();

    const joins: AlgorithmJoinsMutationDtoType['joins'] =
      algorithmSourceNodes.map((node, index) => {
        const { algorithmSourceId, joinTypeCode, id } = node.data;
        const join: AlgorithmJoinsMutationDtoType['joins'][number] = {
          id,
          joinTypeCode,
          algorithmSourceId,
          algorithmJoinItems: [],
          sequenceNumber: index + 1,
        };
        joinsByNodeId.set(node.id as UUIDType, join);
        return join;
      });

    const mappings: AlgorithmMappingMutationDtoType[] = [];

    nodes.forEach((node) => {
      if (node.type === 'algorithmMapping') {
        mappings.push(
          AlgorithmMappingMutationDtoSchema.parse(node.data.algorithmMapping),
        );

        edgesByTargetId.get(node.id as UUIDType)?.forEach((edge) => {
          const sourceNode = getFromMapSafely(nodesById, edge.source);
          const joinNodeId = edge.data?.joinNodeId as UUIDType | undefined;
          const { alias: algorithmMappingAlias } = node.data.algorithmMapping;

          const ownerNode = resolveOwnerNode({
            edgeSourceNode: sourceNode,
            originalOwnerNode: joinNodeId && nodesById.get(joinNodeId),
            allAlgorithmSourceNodes: algorithmSourceNodes,
          });
          const ownerJoin =
            ownerNode && joinsByNodeId.get(ownerNode.id as UUIDType);

          const itemId =
            ownerNode?.id === joinNodeId ? edge.data?.itemId : undefined;

          if (!ownerJoin) {
            return;
          }

          if (sourceNode.type === 'algorithmSource') {
            const { itemIndex: sourceItemIndex } = parseHandleId(
              edge.sourceHandle,
            );
            const sourceItem = sourceNode.data.items[sourceItemIndex];
            if ('dimensionCode' in sourceItem) {
              ownerJoin.algorithmJoinItems.push({
                id: itemId,
                algorithmSourceId: sourceNode.data.algorithmSourceId,
                sourceDimension: decodeDimensionCodeValue(
                  sourceItem.dimensionCode,
                ).dimensionCode,
                algorithmMappingAlias,
              });
            }
          } else if (sourceNode.type === 'constant') {
            ownerJoin.algorithmJoinItems.push({
              id: itemId,
              sourceValue: sourceNode.data.constantValue,
              algorithmMappingAlias,
            });
          }
        });
      } else if (node.type === 'algorithmSource') {
        const join = joinsByNodeId.get(node.id as UUIDType)!;
        const targetEdges = edgesByTargetId.get(node.id);

        if (!targetEdges) {
          return;
        }

        const targetEdgesByHandleId = getMapBy(targetEdges, 'targetHandle');

        join.algorithmJoinItems.push(
          ...node.data.items.reduce(
            (joinItems, item, itemIndex) => {
              const handleId = getHandleId(node.id, itemIndex, 'target');
              const targetEdge = targetEdgesByHandleId.get(handleId);

              if (!targetEdge) {
                return joinItems;
              }

              const sourceNode = nodesById.get(targetEdge.source)!;

              if (sourceNode.type === 'algorithmMapping') {
                const { alias: algorithmMappingAlias } =
                  sourceNode.data.algorithmMapping;
                return joinItems.concat(
                  'dimensionCode' in item
                    ? {
                        id: item.id,
                        algorithmMappingAlias,
                        targetDimension: decodeDimensionCodeValue(
                          item.dimensionCode,
                        ).dimensionCode,
                      }
                    : {
                        id: item.id,
                        algorithmMappingAlias,
                        targetValue: item.constantValue,
                      },
                );
              } else if (
                sourceNode.type === 'constant' &&
                'dimensionCode' in item
              ) {
                return joinItems.concat({
                  id: item.id,
                  sourceValue: sourceNode.data.constantValue,
                  targetDimension: decodeDimensionCodeValue(item.dimensionCode)
                    .dimensionCode,
                });
              } else if (sourceNode.type === 'algorithmSource') {
                const { itemIndex: sourceItemIndex } = parseHandleId(
                  targetEdge.sourceHandle,
                );
                const sourceItem = sourceNode.data.items[sourceItemIndex];

                if ('dimensionCode' in sourceItem) {
                  return joinItems.concat(
                    'dimensionCode' in item
                      ? {
                          id: item.id,
                          algorithmSourceId: sourceNode.data.algorithmSourceId,
                          sourceDimension: decodeDimensionCodeValue(
                            sourceItem.dimensionCode,
                          ).dimensionCode,
                          targetDimension: decodeDimensionCodeValue(
                            item.dimensionCode,
                          ).dimensionCode,
                        }
                      : {
                          id: item.id,
                          algorithmSourceId: sourceNode.data.algorithmSourceId,
                          sourceDimension: decodeDimensionCodeValue(
                            sourceItem.dimensionCode,
                          ).dimensionCode,
                          targetValue: item.constantValue,
                        },
                  );
                }
                throw new Error('Invalid source item type');
              }

              return joinItems;
            },
            [] as AlgorithmJoinsMutationDtoType['joins'][number]['algorithmJoinItems'],
          ),
        );
      }
    });

    return { joins, mappings };
  });
